import { HelpBtn } from './HelpBtn';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, MapPin, Network } from 'lucide-react';
import { openNetShare } from './NetShare';
import { NST } from '../lib/netShareText';
import { uid } from '../lib/model';
import { buildGraph, pathBetween, pathSections, pointOnPath, velOf, corrX } from '../lib/netGraph';
import { num } from '../lib/utility';

export const openCorr = () => window.dispatchEvent(new Event('giss-corr-open'));
export const hilite = (d) => window.dispatchEvent(new CustomEvent('giss-hl', { detail: d }));
const N = ({ testid, value, ph, onChange }) => <input inputMode="decimal" data-testid={testid} value={value ?? ''} placeholder={ph} onChange={(e) => onChange(e.target.value.replace(',', '.'))} />;
const m = (v, d = 1) => `${(v ?? 0).toFixed(d).replace('.', ',')} m`;

function Sections({ t, secs, vOv, setV }) {
  return (
    <table className="cc-tab" data-testid="corr-sections">
      <thead><tr><th>{t.ccSec}</th><th>{t.plMat}</th><th>DN</th><th>{t.swLen}</th><th>v (m/s)</th></tr></thead>
      <tbody>{secs.map((s, i) => (
        <tr key={s.key} data-testid={`corr-sec-${i}`}><td>{s.name}</td><td>{s.mat || '–'}</td><td>{s.dn || '–'}</td><td>{m(s.len)}</td>
          <td><N testid={`corr-v-${i}`} value={vOv[s.key] ?? ''} ph={String(s.v0)} onChange={(v) => setV({ ...vOv, [s.key]: v })} /></td></tr>
      ))}</tbody>
    </table>
  );
}

// leak correlation over mixed sections: sensor A/B -> network path -> distance or Δt -> leak pin
export default function CorrCalc({ t, project, updateProject, mapRef }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ a: '', b: '', inA: '', inB: '', vIn: '1250', mode: 'dist', val: '' });
  const [vOv, setV] = useState({});
  useEffect(() => { const on = () => setOpen(true); window.addEventListener('giss-corr-open', on); return () => window.removeEventListener('giss-corr-open', on); }, []);
  const G = useMemo(() => buildGraph(project.pipes, project.fittings, ['water', 'fire', 'other']), [project.pipes, project.fittings]);
  const nodes = (project.fittings || []).filter((x) => G.adj['f:' + x.id]);
  const path = f.a && f.b && f.a !== f.b ? pathBetween(G, 'f:' + f.a, 'f:' + f.b) : null;
  const vIn = num(f.vIn) || 1250;
  const secs = [
    ...(num(f.inA) ? [{ key: 'inA', name: `${t.ccIndoor} A`, len: num(f.inA), v0: vIn }] : []),
    ...(path ? pathSections(path).map((s, i) => ({ key: s.pipe.id + i, name: s.pipe.name || t.objKind_pipe, mat: s.pipe.material || s.pipe.cat?.name, dn: s.pipe.dn, len: s.len, v0: velOf(s.pipe.material || s.pipe.cat?.name) })) : []),
    ...(num(f.inB) ? [{ key: 'inB', name: `${t.ccIndoor} B`, len: num(f.inB), v0: vIn }] : []),
  ].map((s) => ({ ...s, v: num(vOv[s.key]) || s.v0 }));
  const L = secs.reduce((s, x) => s + x.len, 0);
  const val = num(f.val);
  const x = val == null || !path ? null : Math.min(L, Math.max(0, f.mode === 'dist' ? val : corrX(secs, val)));
  const px = x == null ? null : Math.min(path.len, Math.max(0, x - (num(f.inA) || 0)));
  const pt = px == null ? null : pointOnPath(G, path, px);
  useEffect(() => { if (open && path) hilite({ lines: path.edges.map((e) => [G.pos[e.from], G.pos[e.to]]), pins: pt ? [pt] : [] }); }, [open, f, vOv]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const close = () => { hilite(null); setOpen(false); };
  const name = (id) => project.fittings.find((y) => y.id === id)?.name || '?';
  const place = () => {
    const corr = { a: f.a, b: f.b, aName: name(f.a), bName: name(f.b), L, mode: f.mode, val, x, inA: num(f.inA), inB: num(f.inB), secs: secs.map(({ name: n, mat, dn, len, v }) => ({ name: n, mat, dn, len, v })), t: Date.now() };
    const note = `${t.ccTitle}: ${corr.aName} ↔ ${corr.bName} · ${m(x)} ${t.ccFromA}`;
    const lid = uid('fit');
    updateProject((p) => ({ ...p, fittings: [...p.fittings, { id: lid, type: 'leak', state: 'defect', severity: 'medium', name: `${t.fit_leak} K${(p.fittings.filter((q) => q.corr).length || 0) + 1}`, note, lat: pt[0], lon: pt[1], corr, media: [], memos: [] }] }));
    mapRef.current?.fitAll([pt]);
    toast.success(t.ccPlaced, { action: { label: `🌊 ${(NST[localStorage.getItem('baustelle_app_lang')] || NST.en).aqBtn}`, onClick: () => window.dispatchEvent(new CustomEvent('giss-leak-aqua', { detail: { id: lid } })) }, duration: 9000 });
    close();
  };
  const set = (p) => setF((o) => ({ ...o, ...p }));
  return createPortal(
    <div className="editor-backdrop" data-testid="corr-dialog">
      <div className="editor cc-ed">
        <div className="row pf-head"><h4>🎧 {t.ccTitle}</h4><HelpBtn k="n:water_extra" testid="corr-help" title={t.helpHere} /><button className="icon-btn" data-testid="corr-close-btn" onClick={close}><X size={18} /></button></div>
        <div className="sw-grid">
          {['a', 'b'].map((k) => (
            <label key={k}>{t.ccSensor} {k.toUpperCase()}<select data-testid={`corr-sensor-${k}`} value={f[k]} onChange={(e) => set({ [k]: e.target.value })}>
              <option value="">…</option>{nodes.map((n) => <option key={n.id} value={n.id}>{n.name || n.type}</option>)}
            </select></label>
          ))}
          <label>{t.ccIndoor} A (m)<N testid="corr-in-a" value={f.inA} ph="0" onChange={(inA) => set({ inA })} /></label>
          <label>{t.ccIndoor} B (m)<N testid="corr-in-b" value={f.inB} ph="0" onChange={(inB) => set({ inB })} /></label>
          <label>v {t.ccIndoor} (m/s)<N testid="corr-v-in" value={f.vIn} ph="1250" onChange={(v) => set({ vIn: v })} /></label>
        </div>
        {!nodes.length && <p className="muted" data-testid="corr-no-nodes">{t.ccNoNodes}</p>}
        {!project.pipes?.length && <button className="btn btn-outline btn-sm" data-testid="corr-net-open-btn" onClick={() => { setOpen(false); openNetShare(); }}><Network size={14} /> {(NST[localStorage.getItem('baustelle_app_lang')] || NST.en).btn}</button>}
        {f.a && f.b && !path && <p className="pr-alert low" data-testid="corr-no-path">{t.ccNoPath}</p>}
        {path && <><p className="sw-res" data-testid="corr-total">{t.ccTotal}: <b>{m(L)}</b> ({secs.length} {t.ccSec})</p><Sections t={t} secs={secs} vOv={vOv} setV={setV} /></>}
        {path && (
          <div className="sw-grid">
            <div className="chip-group">
              <button className={`chip seg ${f.mode === 'dist' ? 'on' : ''}`} data-testid="corr-mode-dist" onClick={() => set({ mode: 'dist' })}>{t.ccModeDist}</button>
              <button className={`chip seg ${f.mode === 'dt' ? 'on' : ''}`} data-testid="corr-mode-dt" onClick={() => set({ mode: 'dt' })}>{t.ccModeDt}</button>
            </div>
            <label>{f.mode === 'dist' ? `${t.ccDistA} (m)` : 'Δt (ms)'}<N testid="corr-val" value={f.val} ph={f.mode === 'dist' ? 'm' : 'ms'} onChange={(v) => set({ val: v })} /></label>
          </div>
        )}
        {path && f.mode === 'dt' && <small className="muted">{t.ccDtHint}</small>}
        {x != null && <p className="sw-res" data-testid="corr-result">📍 {t.ccResult}: <b>{m(x, 2)}</b> {t.ccFromA} · <b>{m(L - x, 2)}</b> {t.ccFromB}{x < (num(f.inA) || 0) || x > L - (num(f.inB) || 0) ? ` · ⚠️ ${t.ccInIndoor}` : ''}</p>}
        <button className="btn btn-red" data-testid="corr-place-btn" disabled={!pt} onClick={place}><MapPin size={16} /> {t.ccPlace}</button>
      </div>
    </div>,
    document.body,
  );
}
